import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BaseResponse, BaseResponseSingle } from "../../typings/interfaces";
import {
  IColorPintag,
  IServicePintag,
  IValidatePinTagsRequest,
  IValidatePinTagsResponse,
} from "../../typings/interfaces/pos/pintag";
import {
  CREATE_COLOR_PINTAG,
  CREATE_SERVICE_PINTAG,
  DELETE_COLOR_PINTAG,
  DELETE_SERVICE_PINTAG,
  GET_ALL_COLOR_PINTAGS,
  GET_ALL_SERVICE_PINTAGS,
  REORDER_PINTAGS,
  UPDATE_COLOR_PINTAG,
  UPDATE_COLOR_PINTAG_ORDER,
  UPDATE_SERVICE_PINTAG,
  UPDATE_BILL_PINTAG,
  VALIDATE_PINTAGS,
  PINTAG_SEARCH,
  GET_RACK,
} from "../../configs";
import {
  ICreateColorPintagRequest,
  ICreateServicePintagRequest,
  IReOrderColorPintagRequest,
} from "../../typings/interfaces/requests/pintag-requests";

export interface IBill {
  billStatus: string;
  id: number;
  paymentFilterStatus: string;
  pickingFilterStatus: string;
  billAmount: number;
  paidAmount: number | null;
  totalItemCount: number;
  customerId: number;
  billId: string;
  billString: string;
  serviceTypeId: string;
  cashierId: string;
  createdTime: string;
  discountAmount: number | null;
  discountId: number | null;
  dueDate: string | null;
  pintagColorDescription: string | null;
  cashierName: string;
  rackNumber: string | null;
  pintagColor: string | null;
  lastPickupDate: string | null;
  pintagTags: string | null;
  lastPaymentDate: string;
  description?: string | null;
  dropCashier?: string | null;
  isVoided?: boolean;
  isRefunded?: boolean;
}

export interface IFilteredBillRequest {
  customerId?: number;
  pintagColor?: string;
  pintagTag?: (string | number)[];
  invoice?: (string | number)[];
  generalQuery?: string;
  dueDateFrom?: string;
  rackLocation?: string;
  page?: number;
  size?: number;
}

export interface IRack {
  id: number;
  rackLocation: string;
}

export interface IUpdateBillPintagRequest {
  billId: number;
  // Either the palette hex color or the alias identifies the pin tag color;
  // both are matched against the loaded color-pintag list (see resolution
  // below) since callers (e.g. AssignPinTagModal) only carry the hex value.
  color: string | null;
  tags: string[];
}

export interface IUpdateBillPintagResult {
  billId: number;
  pintagColor: string;
  pintagColorDescription: string | null;
  pintagTags: string;
}

export const getAllColorPinTagsAction = createAsyncThunk(
  "pintag/color/all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IColorPintag>>(
        GET_ALL_COLOR_PINTAGS
      );
      // The API answers HTTP 200 + FAILED on errors; reject so the modal retries
      if (response.data?.status !== "SUCCESS") {
        return rejectWithValue({ message: response.data?.message || "Could not load pin tag colours" });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const createNewColorPinTagAction = createAsyncThunk(
  "pintag/color/create",
  async (
    { color, description, alias, orderIndex }: ICreateColorPintagRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponseSingle<IColorPintag>>(
        CREATE_COLOR_PINTAG,
        { color, description, alias, orderIndex }
      );
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const updateColorPinTagAction = createAsyncThunk(
  "pintag/color/update",
  async (
    { id, color, description, alias, orderIndex, day }: IColorPintag,
    { rejectWithValue }
  ) => {
    try {
      // Ensure day field is always included in the request (can be null/undefined)
      const payload: any = { id, color, description, alias, orderIndex };
      if (day !== undefined) {
        payload.day = day;
      } else {
        payload.day = null;
      }
      
      console.log("Sending update request with payload:", payload);
      
      const response = await axios.put<BaseResponseSingle<IColorPintag>>(
        UPDATE_COLOR_PINTAG,
        payload
      );
      // The API answers failures with HTTP 200 and success: false
      if (response.data?.success === false) {
        return rejectWithValue({ message: response.data.message });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const deleteColorPinTagAction = createAsyncThunk(
  "pintag/color/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.delete<any>(DELETE_COLOR_PINTAG(id));
      // The API answers failures with HTTP 200 and success: false
      if (response.data?.success === false) {
        return rejectWithValue({ message: response.data.message });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const createNewServicePinTagAction = createAsyncThunk(
  "pintag/service/create",
  async (
    { serviceTypeId, description, tagCount }: ICreateServicePintagRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponseSingle<IServicePintag>>(
        CREATE_SERVICE_PINTAG,
        { serviceTypeId, description, tagCount }
      );
      // The API answers failures with HTTP 200 and success: false
      if (response.data?.success === false) {
        return rejectWithValue({ message: response.data.message });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const updateServicePinTagAction = createAsyncThunk(
  "pintag/service/update",
  async (
    { id, serviceTypeId, description, tagCount }: IServicePintag,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<BaseResponseSingle<IServicePintag>>(
        UPDATE_SERVICE_PINTAG,
        { id, serviceTypeId, description, tagCount }
      );
      // The API answers failures with HTTP 200 and success: false
      if (response.data?.success === false) {
        return rejectWithValue({ message: response.data.message });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const deleteServicePinTagAction = createAsyncThunk(
  "pintag/service/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.delete<any>(DELETE_SERVICE_PINTAG(id));
      // The API answers failures with HTTP 200 and success: false
      if (response.data?.success === false) {
        return rejectWithValue({ message: response.data.message });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const getAllServicePinTagsAction = createAsyncThunk(
  "pintag/service/all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IServicePintag>>(
        GET_ALL_SERVICE_PINTAGS
      );
      if (response.data?.status !== "SUCCESS") {
        return rejectWithValue({ message: response.data?.message || "Could not load pin tag books" });
      }
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const validatePinTagsAction = createAsyncThunk(
  "pintag/color/validate",
  async (payload: IValidatePinTagsRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ body: IValidatePinTagsResponse }>(
        VALIDATE_PINTAGS,
        payload
      );
      return { response: response.data.body };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);

export const getFilteredBillsAction = createAsyncThunk(
  "bill/filtered",
  async (params: IFilteredBillRequest, { rejectWithValue }) => {
    try {
      const pageSize = params.size || 15;
      const page = params.page || 0;
      
      const updatedParams = {
        ...params,
        size: pageSize,
        page: page,
      };
      
      const url = PINTAG_SEARCH(updatedParams);
      const response = await axios.get<BaseResponse<IBill>>(url);

      return { 
        response: response.data,
        isReset: params.page === 0 || !params.page 
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const getAllRacksAction = createAsyncThunk(
  "rack/all",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IRack>>(`${GET_RACK}`);
      return { response: response.data };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// Persists a pin tag change for a single existing bill from the
// /pos/search-invoice "Update Pintag" screen (pintag-table.tsx), independent
// of the ambient cart-session flow. The modal only carries the palette's hex
// `color`, but the backend upsert (PintagDto) expects the color's `alias` -
// resolve it here the same way cartAction.ts's `resolveColorAlias` does for
// new-bill creation, using the already-loaded color-pintag list.
export const updateBillPintagAction = createAsyncThunk<
  IUpdateBillPintagResult,
  IUpdateBillPintagRequest,
  { rejectValue: string }
>(
  "bill/pintag/update",
  async ({ billId, color, tags }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as {
        pintag: { getColorPintags: BaseResponse<IColorPintag> | null };
        auth: { user?: { id?: number } | null };
      };
      const colorPintags = state.pintag?.getColorPintags?.body ?? [];
      const normalizedColor = (color ?? "").trim().toLowerCase();
      const matched = colorPintags.find(
        (item) =>
          item.color?.toLowerCase() === normalizedColor ||
          item.alias?.toLowerCase() === normalizedColor
      );

      if (!matched) {
        return rejectWithValue(
          `Unable to resolve pin tag color "${color}". Please refresh and try again.`
        );
      }

      // The backend returns HTTP 200 even for business-rule failures (bill
      // not found, voided bill, unknown color) - it only signals failure via
      // `success: false` in the body, not the status code - so this must be
      // checked explicitly rather than relying on axios rejecting.
      const response = await axios.put<
        BaseResponseSingle<{ color: string; tags: string[] }>
      >(UPDATE_BILL_PINTAG(billId), {
        color: matched.alias,
        tags,
        // Shown as who changed it on the bill history
        cashierId: state.auth?.user?.id,
      });

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data?.message || "Failed to update pin tag"
        );
      }

      return {
        billId,
        pintagColor: matched.color,
        pintagColorDescription: matched.description ?? null,
        pintagTags: tags.join(","),
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Failed to update pin tag"
      );
    }
  }
);

export const reorderPintagColorsAction = createAsyncThunk(
  "pintag/color/reorder-new",
  async (reorderedList: IReOrderColorPintagRequest[], { rejectWithValue }) => {
    try {
      const response = await axios.post<BaseResponse<IColorPintag>>(
        REORDER_PINTAGS,
        reorderedList
      );
      return { response: response.data };
    } catch (err) {
      if (!err) {
        throw err;
      }
      return rejectWithValue(err);
    }
  }
);
